import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Clock, Sun, Moon, Database, ArrowUpRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { formatAmount } from '../../lib/format';

type WalletType = 'cash' | 'bank' | 'crypto' | 'payment_system';

interface WalletBalance {
  walletId: string;
  walletName: string;
  walletType: WalletType;
  currency: string;
  snapshotId: string | null;
  openingBalance: string | null;
  closingBalance: string | null;
  totalIn: string;
  totalOut: string;
  check: string | null;
  referenceType: 'morning' | 'prev_evening' | 'none';
  referenceValue: string | null;
  referenceTime: string | null;
}

export default function DailyBalancesPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { t: tr } = useTranslation('reports');
  const queryClient = useQueryClient();
  const { currentEntity } = useEntity();
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const todayDefault = new Date().toISOString().split('T')[0];
  const selectedDate = searchParams.get('date') || todayDefault;
  const myOnly = searchParams.get('myOnly') === 'true';
  const timeMode = (searchParams.get('timeMode') as 'full' | 'working') || 'full';

  const setSelectedDate = useCallback((date: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (date === todayDefault) next.delete('date'); else next.set('date', date);
      return next;
    }, { replace: true });
  }, [setSearchParams, todayDefault]);

  const setMyOnly = useCallback((val: boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val) next.set('myOnly', 'true'); else next.delete('myOnly');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setTimeMode = useCallback((mode: 'full' | 'working') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (mode === 'full') next.delete('timeMode'); else next.set('timeMode', mode);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [editedBalances, setEditedBalances] = useState<Record<string, { opening?: string; closing?: string }>>({});

  const canEdit = user?.role === 'admin' || user?.role === 'accountant' || user?.role === 'cashier';

  // Check if selected date is today
  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === todayStr;

  const { data, isLoading } = useQuery({
    queryKey: ['daily-balances', currentEntity?.id, selectedDate, myOnly, isToday ? timeMode : 'full'],
    queryFn: async () => {
      const params = new URLSearchParams({ date: selectedDate });
      if (myOnly) params.set('myOnly', 'true');
      // Only send timeMode when today is selected
      if (isToday && timeMode === 'working') {
        params.set('timeMode', 'working');
      }
      const res = await fetch(`/api/balances/daily?${params}`, {
        credentials: 'include',
        headers: { 'X-Entity-Id': currentEntity!.id },
      });
      if (!res.ok) throw new Error('Failed to load balances');
      return res.json();
    },
    enabled: !!currentEntity,
  });

  // Fetch working balances from reports/balances API
  const { data: balancesData } = useQuery({
    queryKey: ['report-balances', currentEntity?.id],
    queryFn: async () => {
      const res = await fetch('/api/reports/balances', {
        credentials: 'include',
        headers: { 'X-Entity-Id': currentEntity!.id },
      });
      if (!res.ok) throw new Error('Failed to load balances');
      return res.json();
    },
    enabled: !!currentEntity,
  });

  // Build walletId → working balance map
  const workingBalanceMap = useMemo(() => {
    const map: Record<string, { balance: string; currency: string }> = {};
    if (!balancesData?.byType) return map;
    for (const wallets of Object.values(balancesData.byType) as any[][]) {
      for (const w of wallets) {
        map[w.walletId] = { balance: w.balance, currency: w.currency };
      }
    }
    return map;
  }, [balancesData]);

  const saveMutation = useMutation({
    mutationFn: async (balances: Array<{ walletId: string; openingBalance?: string; closingBalance?: string }>) => {
      const res = await fetch('/api/balances/daily/bulk', {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          'X-Entity-Id': currentEntity!.id,
        },
        body: JSON.stringify({ date: selectedDate, balances }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to save balances');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-balances'] });
      setEditedBalances({});
      success('Balances saved');
    },
    onError: (err: Error) => showError(err.message),
  });

  const handleSave = () => {
    const toSave = Object.entries(editedBalances).map(([walletId, vals]) => ({
      walletId,
      // Empty string = "clear the balance" → send '0' to DB (0 = not entered in reference point logic)
      openingBalance: vals.opening === '' ? '0' : vals.opening,
      closingBalance: vals.closing === '' ? '0' : vals.closing,
    }));
    if (toSave.length === 0) return;
    saveMutation.mutate(toSave);
  };

  const updateBalance = (walletId: string, field: 'opening' | 'closing', rawValue: string) => {
    // Normalize comma to dot for decimal input, allow empty string as "cleared"
    const value = rawValue.replace(',', '.');
    setEditedBalances(prev => ({
      ...prev,
      [walletId]: { ...prev[walletId], [field]: value },
    }));
  };

  const formatNumber = (val: string | null) => {
    if (val === null) return '-';
    const num = parseFloat(val);
    return num.toFixed(2);
  };

  if (!currentEntity) return null;

  const wallets: WalletBalance[] = data?.wallets || [];
  const walletsByType = wallets.reduce((acc, w) => {
    if (!acc[w.walletType]) acc[w.walletType] = [];
    acc[w.walletType].push(w);
    return acc;
  }, {} as Record<WalletType, WalletBalance[]>);

  const hasChanges = Object.keys(editedBalances).length > 0;

  // Determine dominant reference type for the info banner
  const referenceTypes = useMemo(() => {
    const types = new Set(wallets.map(w => w.referenceType));
    return types;
  }, [wallets]);

  const hasAnyReference = referenceTypes.has('morning') || referenceTypes.has('prev_evening');

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{tr('dailyBalances') || 'Daily Balances'}</h1>
        <div className="flex items-center gap-4">
          {user?.role === 'cashier' && (
            <div className="inline-flex bg-surface rounded-lg p-1 border border-border shadow-sm">
              <button
                id="daily-balances-scope-all"
                className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-all ${!myOnly ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
                onClick={() => setMyOnly(false)}
              >
                All
              </button>
              <button
                id="daily-balances-scope-my"
                className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-all ${myOnly ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
                onClick={() => setMyOnly(true)}
              >
                My Only
              </button>
            </div>
          )}

          {/* Time mode toggle — only visible when today is selected */}
          {isToday && (
            <div className="inline-flex bg-surface rounded-lg p-1 border border-border shadow-sm">
              <button
                id="daily-balances-time-full"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-all ${timeMode === 'full' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
                onClick={() => setTimeMode('full')}
                title="00:00 – 24:00 Madrid"
              >
                <Clock size={14} />
                00–24
              </button>
              <button
                id="daily-balances-time-working"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-all ${timeMode === 'working' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
                onClick={() => setTimeMode('working')}
                title="10:00 – 18:00 Madrid (working hours)"
              >
                <Clock size={14} />
                10–18
              </button>
            </div>
          )}

          <input
            type="date"
            className="form-input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ width: 180 }}
          />
          {canEdit && hasChanges && (
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? tc('actions.loading') : tc('actions.save')}
            </button>
          )}
        </div>
      </div>

      {/* Reference point info banner */}
      {hasAnyReference && (
        <div className="card mb-4">
          <div className="flex items-center gap-6 px-4 py-3 flex-wrap">
            {referenceTypes.has('morning') && (
              <span className="flex items-center gap-2 text-sm">
                <Sun size={16} className="text-ctp-yellow" />
                <span className="font-medium">Утро (10:00)</span>
                <span className="text-foreground-muted">— опорная точка на {selectedDate}</span>
              </span>
            )}
            {referenceTypes.has('prev_evening') && !referenceTypes.has('morning') && (
              <span className="flex items-center gap-2 text-sm">
                <Moon size={16} className="text-ctp-blue" />
                <span className="font-medium">Вечер пред. дня (18:00)</span>
                <span className="text-foreground-muted">— опорная точка</span>
              </span>
            )}
            {referenceTypes.has('morning') && referenceTypes.has('prev_evening') && (
              <span className="flex items-center gap-2 text-sm text-foreground-muted">
                <Moon size={14} className="text-ctp-blue" />
                Часть кошельков: Вечер пред. дня (18:00)
              </span>
            )}
            {referenceTypes.has('none') && (
              <span className="flex items-center gap-2 text-sm text-foreground-muted">
                <Database size={14} />
                Часть кошельков: нет снэпшота — полный баланс леджера
              </span>
            )}
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="flex items-center gap-6 px-4 py-3">
          <span className="flex items-center gap-2 text-sm">
            <span className="inline-block px-2 py-0.5 rounded font-semibold text-[13px] bg-green-100 text-green-800">0.00</span> = {tr('balanced') || 'Balanced'}
          </span>
          <span className="flex items-center gap-2 text-sm">
            <span className="inline-block px-2 py-0.5 rounded font-semibold text-[13px] bg-red-100 text-red-600">-50.00</span> = {tr('discrepancy') || 'Discrepancy'}
          </span>
          <span className="text-sm text-foreground-muted">
            {tr('checkFormula') || 'Check = Closing - (Opening + IN - OUT)'}
          </span>
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted">{tc('actions.loading')}</p>
      ) : (
        <div className="balance-sections">
          {(['cash', 'bank', 'crypto', 'payment_system'] as WalletType[]).map(type => {
            const typeWallets = walletsByType[type] || [];
            if (typeWallets.length === 0) return null;

            return (
              <div key={type} className="card mb-4">
                <h3 className="flex items-center gap-3 px-4 py-3 m-0 border-b border-border">
                  <span className={`badge badge-${type}`}>{t(`wallets.types.${type}`)}</span>
                  <span className="text-foreground-muted font-normal text-sm">({typeWallets.length})</span>
                </h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{tr('wallet') || 'Wallet'}</th>
                      <th>{tr('currency') || 'Currency'}</th>
                      <th>{tr('balance') || 'Balance'}</th>
                      <th>{tr('opening') || 'Opening'}</th>
                      <th>{tr('totalIn') || 'IN'}</th>
                      <th>{tr('totalOut') || 'OUT'}</th>
                      <th>{tr('closing') || 'Closing'}</th>
                      <th>{tr('check') || 'Check'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {typeWallets.map((w) => {
                      const edited = editedBalances[w.walletId] || {};
                      const openingVal = edited.opening !== undefined ? edited.opening : (w.openingBalance || '');
                      const closingVal = edited.closing !== undefined ? edited.closing : (w.closingBalance || '');
                      
                      return (
                        <tr key={w.walletId}>
                          <td>
                            <div className="flex items-center gap-2">
                              <strong>{w.walletName}</strong>
                              {w.referenceType === 'morning' && (
                                <span title={`Утро ${w.referenceTime}`}><Sun size={14} className="text-ctp-yellow" /></span>
                              )}
                              {w.referenceType === 'prev_evening' && (
                                <span title={`Вечер пред. дня ${w.referenceTime}`}><Moon size={14} className="text-ctp-blue" /></span>
                              )}
                              <Link
                                to={`/movements?walletId=${w.walletId}&walletName=${encodeURIComponent(w.walletName)}`}
                                className="text-ctp-blue hover:text-ctp-sapphire transition-colors"
                                title={tr('showMovements') || 'Show movements'}
                              >
                                <ArrowUpRight size={14} />
                              </Link>
                            </div>
                          </td>
                          <td>{w.currency}</td>
                          <td>
                            {(() => {
                              const wb = workingBalanceMap[w.walletId];
                              if (!wb) return <span className="text-foreground-muted">—</span>;
                              const num = parseFloat(wb.balance);
                              const isCrypto = ['USDT', 'USDC', 'BTC', 'ETH', 'BNB', 'SOL', 'XRP'].includes(w.currency);
                              return (
                                <span className="font-semibold text-[13px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatAmount(num, isCrypto ? 'crypto' : 'fiat')}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            {canEdit ? (
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-input px-2 py-1 text-[13px] w-[100px]"
                                value={openingVal}
                                onChange={(e) => updateBalance(w.walletId, 'opening', e.target.value)}
                                placeholder="0.00"
                              />
                            ) : (
                              formatNumber(w.openingBalance)
                            )}
                          </td>
                          <td className="text-green-600">+{formatNumber(w.totalIn)}</td>
                          <td className="text-red-600">-{formatNumber(w.totalOut)}</td>
                          <td>
                            {canEdit ? (
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-input px-2 py-1 text-[13px] w-[100px]"
                                value={closingVal}
                                onChange={(e) => updateBalance(w.walletId, 'closing', e.target.value)}
                                placeholder="0.00"
                              />
                            ) : (
                              formatNumber(w.closingBalance)
                            )}
                          </td>
                          <td>
                            <span className={`inline-block px-2 py-0.5 rounded font-semibold text-[13px] ${w.check !== null && Math.abs(parseFloat(w.check)) < 0.01 ? 'bg-green-100 text-green-800' : w.check !== null ? 'bg-red-100 text-red-600' : ''}`}>
                              {w.check !== null ? formatNumber(w.check) : '-'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
